import { useEffect, useState } from "react";

const CACHE_TTL_MS = 5 * 60 * 1000;

const cache = new Map<string, { at: number; promise: Promise<unknown> }>();

/**
 * One shared promise per key for five minutes, so views that want the same
 * scores make one request. A failed load is evicted and the next call retries.
 */
export function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) {
    return hit.promise as Promise<T>;
  }
  const promise = load();
  cache.set(key, { at: Date.now(), promise });
  promise.catch(() => cache.delete(key));
  return promise;
}

/** Empties the cache; for tests. */
export function clearScorecardCache(): void {
  cache.clear();
}

/** A value loaded through the cache. */
export interface Loadable<T> {
  loading: boolean;
  error?: Error;
  value: T;
}

const toError = (e: unknown) => (e instanceof Error ? e : new Error(String(e)));

/** Loads `key` through the shared cache; an undefined key means "nothing to load". */
export function useCached<T>(
  key: string | undefined,
  empty: T,
  load: () => Promise<T>
): Loadable<T> {
  const [state, setState] = useState<Loadable<T>>({
    loading: !!key,
    value: empty,
  });
  useEffect(() => {
    let cancelled = false;
    if (!key) {
      setState({ loading: false, value: empty });
      return undefined;
    }
    setState({ loading: true, value: empty });
    cached(key, load).then(
      (value) => !cancelled && setState({ loading: false, value }),
      (e) =>
        !cancelled &&
        setState({ loading: false, error: toError(e), value: empty })
    );
    return () => {
      cancelled = true;
    };
    // `key` captures everything `load` depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}
